import { useNavigateBack } from '@iheartjane/menu';
import {
  Box,
  ConditionalWrapper,
  Flex,
  Loading,
  Typography,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { useEffect } from 'react';

import {
  AgreementQRCodeScreen,
  CartCheckoutError,
  CheckoutAgreementsScreen,
  CustomerInfoScreen,
  LoginScreen,
  MFAScreen,
  Modal,
  PaymentSelectionScreen,
  QRCodeCallout,
} from '@jane/kiosk/components';
import {
  useHideVirtualKeyboard,
  useKioskBankLinkQrCode,
  useKioskSpacing,
  useShowJanePayOnKiosk,
} from '@jane/kiosk/hooks';
import {
  SCREEN_NAMES,
  useCheckout,
  useIdleContext,
} from '@jane/kiosk/providers';

import qrPng from '../assets/qr-logo.png';

export const Checkout = () => {
  const { getKioskSpacing } = useKioskSpacing();
  const { setTimerDisabled } = useIdleContext();
  const {
    bankAccounts,
    error,
    errorDetail,
    formMethods,
    screen,
    setError,
    setErrorDetail,
    setSelectedPayment,
    setScreen,
    store,
  } = useCheckout();
  useHideVirtualKeyboard();

  const showJanePay = useShowJanePayOnKiosk();

  const { error: qrCodeError, url: mobileLinkBankUrl } = useKioskBankLinkQrCode(
    {
      enabled: screen.includes('link-bank'),
      source: 'Checkout',
      storeId: store.id,
    }
  );

  const goBack = useNavigateBack(`stores/${store.id}/menu`);

  const returnFromQRCode = () => {
    setScreen(
      screen.includes('payment')
        ? SCREEN_NAMES.payment
        : SCREEN_NAMES.customerInfo
    );
    setTimerDisabled(false);
  };

  // watch for linked bank accounts to close the QR code callout
  useEffect(() => {
    if (bankAccounts?.length > 0) {
      returnFromQRCode();
    }
  }, [bankAccounts]);

  // this sets the selectedPayment to the primary bank account once the aeropay data loads
  useEffect(() => {
    if (bankAccounts?.length > 0 && showJanePay) {
      const account = bankAccounts?.find((account) => account.is_selected);
      if (account?.account_last_4) {
        setSelectedPayment(account.account_last_4);
      }
    }
  }, [bankAccounts, showJanePay]);

  if (error)
    return (
      <CartCheckoutError
        detail={errorDetail}
        error={error}
        onClose={() => {
          setError('');
          setErrorDetail(undefined);
        }}
      />
    );

  const agreementsStep = screen === SCREEN_NAMES.agreements;
  const agreementQrStep = screen === SCREEN_NAMES.agreementQrCode;

  return (
    <Modal
      appId="root"
      contentLabel="Checkout"
      onRequestClose={() => goBack()}
      open
      overflow="visible"
      variant="dialogue"
    >
      {agreementsStep && <CheckoutAgreementsScreen />}
      {agreementQrStep && <AgreementQRCodeScreen />}

      {!agreementsStep && !agreementQrStep && (
        <Modal.Content overflowY="auto" padding={false}>
          <ConditionalWrapper
            condition={[
              SCREEN_NAMES.customerInfo,
              SCREEN_NAMES.payment,
              SCREEN_NAMES.mfa,
            ].includes(screen)}
            wrapper={(children) => (
              <Flex
                flexDirection="column"
                p={getKioskSpacing(32, 40)}
                width={600}
              >
                <Typography mb={getKioskSpacing(24, 16)} variant="h2-semibold">
                  Checkout
                </Typography>

                <Form.BaseForm
                  formMethods={formMethods}
                  name="Cart User Details"
                  onSubmit={() => null}
                >
                  {children}
                </Form.BaseForm>
              </Flex>
            )}
          >
            {screen === SCREEN_NAMES.loading ? (
              <Flex
                flexDirection="column"
                p={getKioskSpacing(32, 40)}
                width={600}
                gap={getKioskSpacing(24, 16)}
              >
                <Typography variant="h2-semibold">Checkout</Typography>
                <Typography>Placing your order...</Typography>
                <Box position="relative" mt={12}>
                  <Loading color="black" size="lg" />
                </Box>
              </Flex>
            ) : screen === SCREEN_NAMES.customerInfo ? (
              <CustomerInfoScreen />
            ) : screen === SCREEN_NAMES.payment ? (
              <PaymentSelectionScreen />
            ) : screen === SCREEN_NAMES.mfa ? (
              <MFAScreen />
            ) : screen.includes('login') ? (
              <LoginScreen onClick={returnFromQRCode} qrCode={qrPng} />
            ) : screen.includes('link-bank') ? (
              <QRCodeCallout
                type="link-bank"
                buttonLabel="Back to checkout"
                onClick={returnFromQRCode}
                qrCode={qrPng}
                url={mobileLinkBankUrl}
                error={qrCodeError}
              />
            ) : (
              <></>
            )}
          </ConditionalWrapper>
        </Modal.Content>
      )}
    </Modal>
  );
};
